import axios from 'axios'
import { useCallback, useEffect, useRef } from 'react'

import { RouteName } from '@/router'

const SECOND = 1 * 1000
const MINUTE = 60 * SECOND
const DEFAULT_CHECK_INTERVAL = 30 * MINUTE

const APP_UPDATE_TIMESTAMP_PARAM = 'app-update-timestamp'

const PROTECTED_ROUTES = [
  RouteName.makeWorksheet,
]

const LOG_PREFIX = 'APP_UPDATE'

const logger = {
  info(...messages) {
    console.info(LOG_PREFIX, ...messages)
  },
}

const getCheckInterval = () => {
  if (import.meta.env.VITE_APP_UPDATE_INTERVAL) {
    return Number(import.meta.env.VITE_APP_UPDATE_INTERVAL)
  }
  return DEFAULT_CHECK_INTERVAL
}

const checkInterval = getCheckInterval()
const needAppUpdateDetection = Boolean(import.meta.env.VITE_APP_UPDATE_DETECTION)

if (needAppUpdateDetection) {
  logger.info('auto update', needAppUpdateDetection)
}

let currentBuildTimestamp: number | undefined

if (import.meta.env.VITE_APP_BUILD_TIMESTAMP) {
  currentBuildTimestamp = Number(import.meta.env.VITE_APP_BUILD_TIMESTAMP)

  if (currentBuildTimestamp) {
    logger.info('current timestamp', currentBuildTimestamp)
  }
}

const fetchLastBuildTimestamp = async () => {
  const { data } = await axios.get<string>('/build-timestamp')
  return Number(data)
}

export const useAppUpdateDetection = () => {
  const timerRef = useRef(0)
  const checkIntervalRef = useRef(checkInterval)

  const checkUpdate = useCallback(async () => {
    if (!currentBuildTimestamp) {
      logger.info('no current timestamp')
      return
    }

    let lastBuildTimestamp: number | undefined

    try {
      lastBuildTimestamp = await fetchLastBuildTimestamp()
    } catch (err) {
      // ignore error
    }

    if (!lastBuildTimestamp) {
      logger.info('no last timestamp')
      return
    }

    const isAppUpdated = lastBuildTimestamp > currentBuildTimestamp

    if (isAppUpdated) {
      logger.info('has updated version')
      const pathname = window.location.pathname
      if (PROTECTED_ROUTES.some((routeName) => pathname.startsWith(routeName))) {
        logger.info('update skipped, protected route')
        return
      }
      const searchParams = new URLSearchParams(window.location.search)
      const appUpdateTimestampParam = searchParams.get(APP_UPDATE_TIMESTAMP_PARAM)

      if (Number(appUpdateTimestampParam) === lastBuildTimestamp) {
        logger.info('update skipped, has timestamp param')
        return
      }
      searchParams.set(APP_UPDATE_TIMESTAMP_PARAM, String(lastBuildTimestamp))
      logger.info('reload')
      window.location.search = searchParams.toString()
    } else {
      logger.info('up-to-date')
    }
  }, [])

  const stopTimer = useCallback(() => {
    window.clearInterval(timerRef.current)
  }, [])

  const startTimer = useCallback(() => {
    if (!needAppUpdateDetection || !currentBuildTimestamp) {
      logger.info('cannot update')
      return
    }

    stopTimer()

    const checkFunc = async () => {
      timerRef.current = window.setTimeout(async function checkFunc() {
        await checkUpdate()
        timerRef.current = window.setTimeout(checkFunc, checkIntervalRef.current)
      }, checkIntervalRef.current)
    }
    checkFunc()

    return () => {
      stopTimer()
    }
  }, [checkUpdate, stopTimer])

  useEffect(() => {
    startTimer()
  }, [startTimer])

  // 이하는 테스트용으로만 쓰임
  interface UpdateCheckIntervalChangeEvent extends Event {
    detail: {
      interval: number
    }
  }

  const handleUpdateCheckIntervalChange = useCallback(
    (e: UpdateCheckIntervalChangeEvent) => {
      const interval = Number(e.detail.interval)
      const minInterval = 1 * MINUTE
      logger.info('check interval change', interval)

      if (!interval || minInterval < minInterval) {
        logger.info('invalid custom interval', interval)
        return
      }
      checkIntervalRef.current = interval
      startTimer()
    },
    [startTimer],
  )

  const UPDATE_CHECK_INTERVAL_CHNAGE = 'update-check-interval-change'

  useEffect(() => {
    document.addEventListener(
      UPDATE_CHECK_INTERVAL_CHNAGE,
      handleUpdateCheckIntervalChange as EventListener,
    )

    return () => {
      document.removeEventListener(
        UPDATE_CHECK_INTERVAL_CHNAGE,
        handleUpdateCheckIntervalChange as EventListener,
      )
    }
  }, [handleUpdateCheckIntervalChange])
}
